import * as yup from 'yup';

import { InvalidScheduleError, Schedule } from './types';

export const schedulesValidationSchema = yup.object({
  startDate: yup
    .string()
    .test({
      name: 'startDate',
      message: 'campaigns.form.scheduling.schedules.errors.start-date-too-late',
      test: (value: string, context: yup.TestContext) => {
        const endDate = new Date(context.parent.endDate);
        const startDate = new Date(value);
        return startDate <= endDate;
      },
    })
    .required(),
  endDate: yup.string().required(),
  timeframe: yup.mixed().oneOf(['open-hours', 'specific-times']).required(),
  schedules: yup
    .array()
    .of(
      yup.object({
        selectedDays: yup.array(),
        timeRanges: yup.array(),
      }),
    )
    .test((value: Schedule[], context: yup.TestContext) => {
      const invalidSections: InvalidScheduleError[] = [];

      value?.forEach((schedule, scheduleIdx) => {
        if (schedule.selectedDays.length <= 0) {
          invalidSections.push({
            scheduleIdx,
            id: 'campaigns.form.scheduling.schedules.errors.weekday-required',
          });
        }

        schedule.timeRanges.forEach((timeRange, timeRangeIdx) => {
          if (timeRange.to - timeRange.from < 60) {
            invalidSections.push({
              scheduleIdx,
              timeRangeIdx,
              id: 'campaigns.form.scheduling.schedules.errors.range-too-short',
            });
          }

          const duplicate = schedule.timeRanges.find((_timeRange, _timeRangeIdx) => {
            return (
              _timeRange.from === timeRange.from && _timeRange.to === timeRange.to && _timeRangeIdx !== timeRangeIdx
            );
          });

          if (duplicate) {
            invalidSections.push({
              scheduleIdx,
              timeRangeIdx,
              id: 'campaigns.form.scheduling.schedules.errors.range-duplicated',
            });
          }

          const overlap = schedule.timeRanges.find((_timeRange, _timeRangeIdx) => {
            return _timeRange.from < timeRange.to && _timeRange.to > timeRange.from && _timeRangeIdx !== timeRangeIdx;
          });

          if (overlap) {
            invalidSections.push({
              scheduleIdx,
              timeRangeIdx,
              id: 'campaigns.form.scheduling.schedules.errors.range-overlap',
            });
          }
        });
      });

      if (invalidSections.length) {
        return context.createError({
          path: 'schedules',
          message: {
            invalidSections,
          },
        });
      }

      return true;
    }),
});

export const getSchedulesValidationSchema = (country?: Country) => {
  if (country === 'ISR') {
    return schedulesValidationSchema.concat(
      yup.object({
        endDate: yup
          .string()
          .test({
            name: 'endDate',
            message: 'campaigns.form.scheduling.schedules.errors.longer-than-35-days',
            test: (value: string, context: yup.TestContext) => {
              const endDate = new Date(value);
              const startDate = new Date(context.parent.startDate);
              const oneDay = 24 * 60 * 60 * 1000;

              return endDate.getTime() - startDate.getTime() < 35 * oneDay;
            },
          })
          .required(),
        startDate: yup
          .string()
          .test({
            name: 'startDate',
            message: 'campaigns.form.scheduling.schedules.errors.delay-for-one-hour',
            test: (value: string) => {
              const startDate = new Date(value);
              const now = new Date();
              const timeDifferenceMs = startDate.getTime() - now.getTime();
              const timeDifferenceHours = timeDifferenceMs / (1000 * 60 * 60);
              return timeDifferenceHours >= 1;
            },
          })
          .required(),
      }),
    );
  }

  return schedulesValidationSchema;
};
